import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEmpty, map, groupBy, find, isString, filter } from 'lodash';
import { Form, Input, Spin, Tooltip, AutoComplete, DatePicker, Select, Checkbox, Radio, InputNumber, Space, Button, Modal, message, Divider, Row, Col, List } from 'antd';
import { LoadingOutlined, InfoCircleOutlined, PlusCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, CloseOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { getProjectById, getProjectNames, updateProject, createProjectCreditingPeriod } from '../../../api/project';
import { getGHGMethodologies, getGHGStandards } from '../../../api/ghg';


import dayjs from 'dayjs';
import { getProjectTypes } from '../../../api/project_type';
const { Option } = Select;

const { RangePicker } = DatePicker;


interface ProjectDetailFormProps {
  form: any;
  updateParentDetails: any;
  dateType: any;
  setDateType: React.Dispatch<React.SetStateAction<any>>;
}

interface MethodologyControlProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  allMethodologies: string[];
}

const MethodologyControl: React.FC<MethodologyControlProps> = ({ value = [], onChange, allMethodologies }) => {
  const [internalSelectedMethodologies, setInternalSelectedMethodologies] = useState<string[]>(value);

  useEffect(() => {
    setInternalSelectedMethodologies(value);
  }, [value]);

  const triggerChange = (newValues: string[]) => {
    if (onChange) {
      onChange(newValues);
    }
  };

  const handleSelectChange = (newValues: string[]) => {
    setInternalSelectedMethodologies(newValues);
    triggerChange(newValues);
  };

  const handleMoveMethodology = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= internalSelectedMethodologies.length) return;

    const newMethodologies = [...internalSelectedMethodologies];
    const item = newMethodologies[index];
    newMethodologies.splice(index, 1);
    newMethodologies.splice(newIndex, 0, item);

    setInternalSelectedMethodologies(newMethodologies);
    triggerChange(newMethodologies);
  };

  const handleRemoveMethodology = (methodologyToRemove: string) => {
    if (internalSelectedMethodologies.length <= 1) {
      message.warning('At least one methodology must be selected');
      return;
    }
    const newMethodologies = internalSelectedMethodologies.filter(m => m !== methodologyToRemove);
    setInternalSelectedMethodologies(newMethodologies);
    triggerChange(newMethodologies);
  };

  return (
    <div
      className="methodology-selection-area"
      style={{
        border: '1px solid #e8e8e8',
        borderRadius: '8px',
        padding: '16px',
        background: '#fdfdfd'
      }}
    >
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}> {/* Outer Flex Container */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}> {/* Left Column */}
          <Select
            mode="multiple"
            showSearch
            value={internalSelectedMethodologies}
            onChange={handleSelectChange}
            style={{ width: '100%' }}
            placeholder="Search and add methodologies..."
            filterOption={(inputValue: string, option: any) =>
              option?.children?.toString().toLowerCase().includes(inputValue.toLowerCase()) || false
            }
            dropdownStyle={{ borderRadius: '6px' }}
          >
            {allMethodologies
              .filter((methodology: string) => !internalSelectedMethodologies.includes(methodology))
              .map((methodology: string) => (
                <Option key={methodology} value={methodology}>{methodology}</Option>
              ))}
          </Select>
        </div>

        <div style={{ flex: 1 }}>
          {internalSelectedMethodologies.length > 1 && (
            <div className="selected-methodologies-list" style={{ marginTop: '0px' }}>
                <div style={{
                  fontSize: '13px',
                  color: '#777',
                  marginBottom: '10px',
                  fontWeight: 500
                }}>
                  Selected Methodologies (Order by Priority):
                </div>
              {internalSelectedMethodologies.map((methodology, index) => (
                <div
                  key={methodology}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 12px',
                    marginBottom: '8px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #f0f0f0',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                    transition: 'box-shadow 0.2s ease-in-out'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.07)'}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.03)'}
                >
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: '#1890ff',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                    fontWeight: 'bold',
                    fontSize: '13px'
                  }}>
                    {index + 1}
                  </div>
                  <div style={{ fontWeight: 500, color: '#333', marginRight: '16px', flexShrink: 0 }}>
                    {methodology}
                  </div>
                  <Space size="small" style={{ marginLeft: 'auto' }}>
                          <Tooltip title="Move Up">
                            <Button
                              type="text"
                              size="small"
                              icon={<ArrowUpOutlined />}
                              disabled={index === 0}
                              onClick={() => handleMoveMethodology(index, 'up')}
                              style={{ color: index === 0 ? '#bfbfbf' : '#555' }}
                            />
                          </Tooltip>
                          <Tooltip title="Move Down">
                            <Button
                              type="text"
                              size="small"
                              icon={<ArrowDownOutlined />}
                              disabled={index === internalSelectedMethodologies.length - 1}
                              onClick={() => handleMoveMethodology(index, 'down')}
                              style={{ color: index === internalSelectedMethodologies.length - 1 ? '#bfbfbf' : '#555' }}
                            />
                          </Tooltip>
                    <Tooltip title="Remove">
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<CloseOutlined style={{ fontSize: '12px' }}/>}
                        onClick={() => handleRemoveMethodology(methodology)}
                      />
                    </Tooltip>
                  </Space>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProjectDetailForm: React.FC<ProjectDetailFormProps> = ({ form, updateParentDetails, dateType, setDateType }) => {
  const { id, creditingPeriodId } = useParams<{ id, creditingPeriodId: string }>();
  const [project, setProject] = useState<any>({});
  const [ghgStandards, setGHGStandards] = useState([]);
  const [ghgMethodologies, setGHGMethodologies] = useState<string[]>([]);
  const [projectTypes, setProjectTypes] = useState<{ group: string, types: string[] }[]>([]);
  const [searchOptions, setSearchOptions] = useState<any[]>([]);
  const [projectNames, setProjectNames] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);


  const navigate = useNavigate();

  useEffect(() => {
    async function fetchProject() {
      setLoading(true);
      try {
        const [
          projectData,
          projects,
          ghgStandards,
          ghgMethodologies,
          projectTypes
        ] = await Promise.all([
          getProjectById(id, Number(creditingPeriodId)),
          getProjectNames(),
          getGHGStandards(),
          getGHGMethodologies(),
          getProjectTypes()
        ]);
        setProject(projectData.response);
        const projectEnrichedNames = projects.projects.filter(project => project.is_live).map(project => {
          return {
            id: project.id,
            name: `${project.ghg_standard.code}${project.project_id}: ${project.name}`,
          };
        });
        setProjectNames(projectEnrichedNames);
        setProjectTypes(
          map(groupBy(projectTypes.response, 'project_group.display_name'), (types, groupName) => ({
            group: groupName,
            types: types.map(type => type.display_name)
          }))
        );
        setSearchOptions(projects.projects.filter(project => project.is_live).map(project => 
            ({ value: `${project.ghg_standard.code}${project.project_id}: ${project.name}`, key: project.id })));
        setGHGStandards(ghgStandards.response.map(ghgStandard => ghgStandard.code));
        setGHGMethodologies(ghgMethodologies.response.map(ghgMethodology => ghgMethodology.display_name));
        const creditingPeriod = projectData.response.project_crediting_periods.find(creditingPeriod => creditingPeriod.crediting_period_id === Number(creditingPeriodId));
        updateParentDetails({
          projectNames: projectEnrichedNames,
          projectTypes: projectTypes.response,
          ghgStandards: ghgStandards.response,
          ghgMethodologies: ghgMethodologies.response,
          projectWorkflow: creditingPeriod.project_workflow,
          project: projectData.response,
        });;
        setLoading(false);
        const projectDuration: any[] = [];
        if (projectData.response.start_year) {
          projectDuration.push(dayjs.utc(projectData.response.start_year));
        } else {
          projectDuration.push(undefined);
        }
        if (projectData.response.end_year) {
          projectDuration.push(dayjs.utc(projectData.response.end_year));
        } else {
          projectDuration.push(undefined);
        }
        form.setFieldsValue({
          projectName: projectData.response.name,
          about: projectData.response.about,
          projectId: projectData.response.project_id,
          ghgStandard: projectData.response.ghg_standard.code,
          ghgMethodology: projectData.response.ghg_methodologies.map(ghgMethodology => ghgMethodology.ghg_methodology.display_name),
          projectType: projectData.response.project_types.map(projectType => projectType.project_type.display_name)[0],
          projectParent: !isEmpty(projectData.response.parent) ? `${projectData.response.parent.ghg_standard.code}${projectData.response.parent.project_id}: ${projectData.response.parent.name}` : '',
          yearRange: projectDuration,
        });
      } catch (error) {
        setLoading(false);
        console.error('Error fetching project:', error);
      }
    }

    fetchProject();
  }, [id, form]);

  const handleSearch = (value: string) => {
    const filteredOptions = projectNames.filter(project =>
      project.name.toLowerCase().includes(value.toLowerCase())
    ).map(projectdata => ({ value: projectdata.name, key: projectdata.id }));
    setSearchOptions(filteredOptions);
  };



  return (
    <>
        <Spin spinning={loading} indicator={<LoadingOutlined style={{ fontSize: 24, color: 'white' }} spin />} fullscreen />
        <div className='flex gap-2'>
          <Form.Item
            label="Registry"
            name="ghgStandard"
            rules={[{ required: true, message: 'Please select GHG Standard!' }]}
          >
            <Select>
              {ghgStandards.map((standard: string) => (
                <Option key={standard} value={standard}>{standard}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Project ID"
            name="projectId"
            rules={[{ required: true, message: 'Please input Project ID!' }]}
          >
            <Input />
          </Form.Item>
        </div>
        <Form.Item
          label={
            <span>
              Project Name&nbsp;
              <Tooltip title={(
                <ul className='mt-2'>
                  <li>Should be 4 or 5 words, around 50 characters long </li>
                  <li>Try to stay as true to the original project name as possible (e.g., delete extraneous words, but don’t change existing ones) </li>
                  <li>If changing original project name, be sure and include full project name in blurb Capitalize all words except conjunctions (and, the, in, etc.)</li>
                </ul>
              )}>
                <InfoCircleOutlined />
              </Tooltip>
            </span>
          }
          name="projectName"
          rules={[
            { required: true, message: 'Please input Project Name!' },
          ]}
        >
          <Input count={{
            show: true
          }}/>
        </Form.Item>
        <div className='w-full'>
          <Form.Item
            label="Methodology"
            name="ghgMethodology"
            className='w-full'
            rules={[{ required: true, message: 'Please select at least one GHG Methodology!' }]}
          >
            <MethodologyControl allMethodologies={ghgMethodologies} />
          </Form.Item>
          <Form.Item
            label="Project Type"
            name="projectType"
            className='w-full'
            rules={[{ required: true, message: 'Please select project type!' }]}
          >
            <Select
              showSearch
              filterOption={(inputValue, option) =>
                option?.children?.toString().toLowerCase().includes(inputValue.toLowerCase()) ? true : false
              }>
              {projectTypes.map((group) => (
                <Select.OptGroup key={group.group} label={<span style={{ fontWeight: 'bold' }}>{group.group}</span>}>
                  {group.types.map((type) => (
                    <Select.Option key={type} value={type}>
                      {type}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              ))}
            </Select>
          </Form.Item>
        </div>
        <Form.Item
          label="About"
          name="about"
          rules={[{ required: false, message: 'Please input about!' }]}
        >
          <Input.TextArea 
            rows={4}
          />
        </Form.Item>
        <Form.Item
          label="Project Parent"
          name="projectParent"
          rules={[{ required: false }]}
        >
          <AutoComplete
            options={searchOptions}
            placeholder="Type project name or GHG standard + project ID"
            onSearch={handleSearch}
          />
        </Form.Item>
        <Form.Item
          label="Project Duration"
          name="yearRange"
          rules={[{ required: false, message: 'Please select Project Duration!' }]}
        >
          <RangePicker />
        </Form.Item>
      </>
  );
};

export default ProjectDetailForm;
